import {
  CreateExclusionGroupEntityFe,
  ExclusionGroupEntityDto,
  ExperienceDto,
  ExperienceListDto,
  MetricArrayType,
} from "@intelligems/ig-types";

import { HistogramMetricData } from "../types/analytics";

export const sortByOrder = (
  a: { order: number },
  b: { order: number }
): number => {
  if (a.order > b.order) {
    return 1;
  }
  if (a.order < b.order) {
    return -1;
  }
  return 0;
};

export const genericSortByOrder = (a: any, b: any): number => {
  if (a.order > b.order) {
    return 1;
  }
  if (a.order < b.order) {
    return -1;
  }
  return 0;
};

export const genericDescSort = (a: any, b: any, sortVal?: string): number => {
  if (a[sortVal ?? "value"] < b[sortVal ?? "value"]) {
    return 1;
  }
  if (a[sortVal ?? "value"] > b[sortVal ?? "value"]) {
    return -1;
  }
  return 0;
};

export const genericAscSort = (a: any, b: any): number => {
  if (a.value > b.value) {
    return 1;
  }
  if (a.value < b.value) {
    return -1;
  }
  return 0;
};

export const genericSort = (a: any, b: any, sortVal: string): number => {
  if (a[sortVal] > b[sortVal]) {
    return 1;
  }
  if (a[sortVal] < b[sortVal]) {
    return -1;
  }
  return 0;
};

export const personalizationMetricSort = (
  experiences: ExperienceDto[] | ExperienceListDto[],
  data: MetricArrayType,
  key: "VISITORS" | "ORDERS" | "TOTAL_REVENUE"
): ExperienceDto[] | ExperienceListDto[] => {
  return [...experiences].sort((a, b) => {
    const aData = data.find((_) => _["EXPERIENCE_ID"] === a.id);
    const bData = data.find((_) => _["EXPERIENCE_ID"] === b.id);
    if (aData && bData) {
      if (aData[key] > bData[key]) {
        return -1;
      }
      if (aData[key] < bData[key]) {
        return 1;
      } else return 0;
    } else if (aData && !bData) {
      return -1;
    } else if (!aData && bData) {
      return 1;
    } else {
      return 0;
    }
  });
};

export const genericBucketAscSort = (a: any, b: any): number => {
  if (a["UNIT_BUCKET"] > b["UNIT_BUCKET"]) {
    return 1;
  }
  if (a["UNIT_BUCKET"] < b["UNIT_BUCKET"]) {
    return -1;
  }
  return 0;
};

const sortOrder = {
  started: 0,
  paused: 1,
  pending: 2,
  ended: 3,
};

export const sortByStatus = (
  experiences: ExperienceDto[] | ExperienceListDto[]
): ExperienceDto[] | ExperienceListDto[] => {
  return [...experiences].sort((a, b) => {
    if (a.status === b.status) {
      if (a.status === "pending") {
        return (
          (b.createdAtTs || Number.MAX_VALUE) -
          (a.createdAtTs || Number.MAX_VALUE)
        );
      } else {
        return (
          (b.startedAtTs || Number.MAX_VALUE) -
          (a.startedAtTs || Number.MAX_VALUE)
        );
      }
    }
    return sortOrder[a.status || "ended"] - sortOrder[b.status || "ended"];
  });
};

export const sortByBucket = (
  a: HistogramMetricData,
  b: HistogramMetricData
) => {
  return a.bucket - b.bucket;
};

export const customExclusionGroupSelectSort = (a, b) => {
  // If both items have exclusionGroupName as "Non-Exclusive Tests", no sorting needed
  if (
    a.exclusionGroupName === "Non-Exclusive Tests" &&
    b.exclusionGroupName === "Non-Exclusive Tests"
  ) {
    return 0;
  }

  // If only one of the items has exclusionGroupName as "Non-Exclusive Tests", it should come first
  if (a.exclusionGroupName === "Non-Exclusive Tests") {
    return -1;
  }
  if (b.exclusionGroupName === "Non-Exclusive Tests") {
    return 1;
  }

  // If none of the items have exclusionGroupName as "Non-Exclusive Tests",
  // then sort alphabetically based on exclusionGroupName
  if (a.exclusionGroupName < b.exclusionGroupName) {
    return -1;
  }
  if (a.exclusionGroupName > b.exclusionGroupName) {
    return 1;
  }
  return 0;
};

const overviewTableSortOrder = {
  Direct: 0,
  Email: 1,
  Referral: 2,
  "Organic Social": 3,
  "Paid Social": 4,
  "Organic Search": 5,
  "Paid Search": 6,
  SMS: 7,
  Google: 8,
  Facebook: 9,
  Instagram: 10,
  TikTok: 11,
  Twitter: 12,
  Other: 13,
  "Other Channel": 14,
  "Other Traffic Source": 15,
};
export const overviewTableRowGroupSort = (data: any[], key: string): any[] => {
  return data.sort((a, b) => {
    return (
      overviewTableSortOrder[a[key] ?? "Other"] -
      overviewTableSortOrder[b[key] ?? "Other"]
    );
  });
};
export const customExclusionGroupListSort = (
  currentExperienceId: string | undefined,
  exclusionGroupEntities:
    | CreateExclusionGroupEntityFe[]
    | ExclusionGroupEntityDto[]
) => {
  const tempExclusionGroupEntities = [...exclusionGroupEntities];
  return tempExclusionGroupEntities.sort((a, b) => {
    if (a.experienceId === undefined && b.experienceId !== undefined) {
      return -1; // Put undefined entityId first
    } else if (a.experienceId !== undefined && b.experienceId === undefined) {
      return 1; // Put undefined entityId last
    } else if (
      a.experienceId === currentExperienceId &&
      b.experienceId !== currentExperienceId
    ) {
      return -1; // Put specified currentExperiment entityId first
    } else if (
      a.experienceId !== currentExperienceId &&
      b.experienceId === currentExperienceId
    ) {
      return 1; // Put specified string entityId last
    } else {
      return 0; // Maintain order
    }
  });
};
